import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';

type Slide = {
  title: string;
  text: string;
  durationMs: number;
  action?: () => void;
};

const speak = (text: string, lang: string) => {
  try {
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = lang === 'ar' ? 'ar-SA' : 'en-US';
    utter.rate = 1;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utter);
  } catch {}
};

const Promo: React.FC = () => {
  const { language, setLanguage } = useLanguage();
  const { currency, setCurrency, formatPrice } = useCurrency();
  const [index, setIndex] = useState(0);
  const timerRef = useRef<number | null>(null);

  const slides: Slide[] = useMemo(
    () => [
      {
        title: 'MIC Green Store',
        text: 'تجربة تسوق إلكترونية متكاملة تدعم تعدد اللغات والعملات.',
        durationMs: 5000,
        action: () => speak('تجربة تسوق إلكترونية متكاملة تدعم تعدد اللغات والعملات.', 'ar'),
      },
      {
        title: 'واجهة المتجر',
        text: 'تصفح التصنيفات والمنتجات بسرعة وأضف للسلة بسهولة.',
        durationMs: 6000,
        action: () => speak('تصفح التصنيفات والمنتجات بسرعة وأضف للسلة بسهولة.', 'ar'),
      },
      {
        title: 'تعدد اللغات',
        text: 'بدّل اللغة بين العربية والإنجليزية ويعرض النظام الأسماء المناسبة.',
        durationMs: 7000,
        action: () => {
          setLanguage('ar');
          speak('بدّل اللغة بين العربية والإنجليزية ويعرض النظام الأسماء المناسبة.', 'ar');
          setTimeout(() => setLanguage('en'), 3500);
          setTimeout(() => setLanguage('ar'), 6000);
        },
      },
      {
        title: 'العملات',
        text: 'اختر العملة المناسبة ويتم تحويل الأسعار تلقائياً.',
        durationMs: 7000,
        action: () => {
          setCurrency('USD');
          speak('اختر العملة المناسبة ويتم تحويل الأسعار تلقائياً.', 'ar');
          setTimeout(() => setCurrency('SAR'), 3000);
          setTimeout(() => setCurrency('AED'), 5500);
        },
      },
      {
        title: 'دعوة للتجربة',
        text: 'ابدأ الآن وجرب النظام أو تواصل معنا.',
        durationMs: 5000,
        action: () => speak('ابدأ الآن وجرب النظام أو تواصل معنا.', 'ar'),
      },
    ],
    [setLanguage, setCurrency]
  );

  useEffect(() => {
    const run = () => {
      const current = slides[index];
      current.action && current.action();
      timerRef.current = window.setTimeout(() => {
        const next = index + 1 >= slides.length ? 0 : index + 1;
        setIndex(next);
      }, current.durationMs);
    };
    run();
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      try {
        window.speechSynthesis.cancel();
      } catch {}
    };
  }, [index, slides]);

  const samplePrice = useMemo(() => formatPrice(100), [formatPrice, currency]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
        background:
          'linear-gradient(135deg, rgba(5,150,105,0.08), rgba(5,150,105,0.18))',
        padding: 24,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 960,
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
          padding: 24,
          border: '1px solid rgba(5,150,105,0.25)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <div style={{ fontSize: 18, color: '#059669' }}>
            {language === 'ar' ? 'العربية' : 'English'}
          </div>
          <div style={{ fontSize: 18, color: '#059669' }}>{currency}</div>
        </div>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#0f172a' }}>
            {slides[index].title}
          </div>
          <div style={{ fontSize: 18, color: '#334155', marginTop: 8 }}>
            {slides[index].text}
          </div>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 16,
          }}
        >
          <div
            style={{
              padding: 16,
              borderRadius: 12,
              border: '1px solid #e2e8f0',
              background: '#f8fafc',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 6, color: '#0f172a' }}>
              المنتج التجريبي
            </div>
            <div style={{ fontSize: 14, color: '#334155', marginBottom: 10 }}>
              سعر العرض: {samplePrice}
            </div>
            <div
              style={{
                display: 'inline-block',
                padding: '6px 12px',
                background:
                  'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#fff',
                borderRadius: 999,
                fontSize: 14,
              }}
            >
              إضافة إلى السلة
            </div>
          </div>
          <div
            style={{
              padding: 16,
              borderRadius: 12,
              border: '1px solid #e2e8f0',
              background: '#f8fafc',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 6, color: '#0f172a' }}>
              التصنيفات
            </div>
            <div style={{ fontSize: 14, color: '#334155' }}>
              أسماء التصنيفات تتبدل وفق اللغة المختارة.
            </div>
          </div>
          <div
            style={{
              padding: 16,
              borderRadius: 12,
              border: '1px solid #e2e8f0',
              background: '#f8fafc',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 6, color: '#0f172a' }}>
              إدارة العملات
            </div>
            <div style={{ fontSize: 14, color: '#334155' }}>
              تحويل الأسعار تلقائي وفق {currency}.
            </div>
          </div>
        </div>
        <div style={{ marginTop: 20, textAlign: 'center', color: '#64748b' }}>
          المشهد {index + 1} من {slides.length}
        </div>
      </div>
    </div>
  );
};

export default Promo;
